import React from 'react';
import { Button, Tooltip } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { useFormikContext } from 'formik';

type SparkFormButtonProps = Omit<ButtonProps, 'type'> & {
  shouldRequireFieldUpdates?: boolean;
  disabledMessage?: string;
  tooltipMessage?: string;
};

function SparkFormButton({
  children,
  shouldRequireFieldUpdates = false,
  disabledMessage,
  tooltipMessage,
  ...rest
}: SparkFormButtonProps) {
  const { isValid, dirty, submitForm } = useFormikContext();
  const { sx, disabled, ...restProps } = rest;

  const isButtonDisabled = React.useMemo(() => {
    if (disabled) {
      return true;
    }

    if (!isValid) {
      return true;
    }
    if (shouldRequireFieldUpdates && !dirty) {
      return true;
    }

    return false;
  }, [dirty, disabled, isValid, shouldRequireFieldUpdates]);

  const getDisabledMessage = () => {
    if (!!disabledMessage && isButtonDisabled) {
      return disabledMessage;
    }
    return children;
  };

  return (
    <Tooltip title={tooltipMessage}>
      <Button
        variant='contained'
        fullWidth
        onClick={submitForm}
        disabled={isButtonDisabled}
        sx={{
          fontWeigh: 700,
          fontFamily: 'Mont-SemiBold',
          fontSize: 16,
          lineHeight: '20.45px',
          '& span': {
            marginTop: '-4px',
          },
          '&.Mui-disabled': {
            pointerEvents: 'all',
          },
          ...sx,
        }}
        {...restProps}
      >
        {getDisabledMessage()}
      </Button>
    </Tooltip>
  );
}

export default SparkFormButton;
